import ReactMarkdown from "react-markdown";
import { useMarketIndicators } from "../../context/MarketIndicatorContext";
import { LuBotMessageSquare } from "react-icons/lu";
import { useEffect, useState } from "react";

const NIFTY_MOMENTUM_PROMPT = `
As of **NSE Market Open – 09:15 AM IST**
Date: {{TODAY_DATE}}
Exchange: NSE India

Act as a short-term momentum trader.

IMPORTANT – STRICT RULES:
- You MUST use REAL NIFTY 50 constituent stock names (Indian NSE)
- Do NOT create placeholder names (e.g., Stock 1, Stock A, etc.)
- Do NOT explain limitations
- Do NOT add disclaimers
- Do NOT add instructional text
- Do NOT add any text before or after the output
- If exact indicator values are unavailable, infer momentum conservatively
- Output MUST be deterministic and structured

TASK:
Create a concise dashboard-style momentum table for **NIFTY 50 stocks**.

Timeframe: Short-term (swing / positional)

For EACH stock, classify:
- RSI (14): Weak (<40), Neutral (40–50), Strong (50–70), Overbought (>70)
- MACD: Bullish / Bearish / Early crossover
- Overall Momentum Status: Momentum / Watchlist / Not Momentum
- Entry View: Buy / Buy on dip / Buy on breakout / Avoid

OUTPUT FORMAT (STRICT):
1. Markdown table ONLY
2. Exactly these columns (no more, no less):
   Stock | RSI Status | MACD Status | Momentum Status | Entry View
3. Every cell MUST be filled
4. 1–3 words per cell
5. Use symbols: ✅ ⚠ ❌
6. One single table

AFTER THE TABLE, add ONLY:
- **Best momentum pick:** <stock names>
- **Conditional / watchlist picks:** <stock names>
- **Stocks to avoid:** <stock names>

No paragraphs. No explanations. No templates.


`;

const extractSummary = (text) => {
  const bestMatch = text.match(/Best momentum pick:\s*(.*)/i);
  const watchMatch = text.match(/Conditional\s*\/\s*watchlist picks:\s*(.*)/i);
  const avoidMatch = text.match(/Stocks to avoid:\s*(.*)/i);

  return {
    best: bestMatch ? bestMatch[1].split(",").map((s) => s.trim()) : [],
    watchlist: watchMatch ? watchMatch[1].split(",").map((s) => s.trim()) : [],
    avoid: avoidMatch ? avoidMatch[1].split(",").map((s) => s.trim()) : [],
  };
};

const MarketTrend = () => {
  const { sendPrompt, messages, loading, error } = useMarketIndicators();
  const [triggered, setTriggered] = useState(false);

  const runMomentumAnalysis = () => {
    setTriggered(true);

    sendPrompt(NIFTY_MOMENTUM_PROMPT, {
      showUser: false,
      queryType: "NIFTY_MOMENTUM_DASHBOARD",
      symbol: "NIFTY50",
    });
  };

  // Get the latest AI response only
  const latestAIResponse = [...messages].reverse().find((m) => m.role === "ai");
  const summary = latestAIResponse?.text
    ? extractSummary(latestAIResponse.text)
    : { best: [], watchlist: [], avoid: [] };

  return (
    <>
      <div
        style={{
          padding: 24,
          maxWidth: 1100,
          margin: "0 auto",
          fontFamily: "Inter, system-ui, sans-serif",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 16,
          }}
        >
          <LuBotMessageSquare size={22} color="#2563eb" />
          <h2 style={{ margin: 0, fontWeight: 600 }}>
            Market Momentum (AI Analysis)
          </h2>
        </div>

        {/* Action Button */}
        <button
          onClick={runMomentumAnalysis}
          disabled={loading}
          style={{
            padding: "10px 18px",
            borderRadius: 8,
            border: "none",
            background: loading ? "#93c5fd" : "#2563eb",
            color: "#fff",
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            marginBottom: 20,
          }}
        >
          {loading ? "Analyzing market…" : "Run AI Analysis"}
        </button>

        {/* Error */}
        {error && (
          <div
            style={{
              color: "#b91c1c",
              background: "#fee2e2",
              padding: "10px 14px",
              borderRadius: 6,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {/* AI Result */}
        {triggered && latestAIResponse && (
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: 16,
              background: "#f8fafc",
              overflowX: "auto",
            }}
          >
            <ReactMarkdown
              components={{
                table: ({ children }) => (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: 12,
                    }}
                  >
                    {children}
                  </table>
                ),
                th: ({ children }) => (
                  <th
                    style={{
                      border: "1px solid #e5e7eb",
                      padding: "8px 10px",
                      background: "#f1f5f9",
                      fontWeight: 600,
                      fontSize: 13,
                      textAlign: "center",
                    }}
                  >
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td
                    style={{
                      border: "1px solid #e5e7eb",
                      padding: "8px 10px",
                      fontSize: 13,
                      textAlign: "center",
                    }}
                  >
                    {children}
                  </td>
                ),
              }}
            >
              {latestAIResponse.text}
            </ReactMarkdown>
          </div>
        )}
      </div>
      {/* ===== AI Decision Summary ===== */}
      <div style={{ marginTop: 24 }}>
        {/* Best Momentum */}
        {summary.best.length > 0 && (
          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <div style={{ minWidth: 170, fontWeight: 600 }}>
              🚀 Best Momentum
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {summary.best.map((stock) => (
                <span
                  key={stock}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 14,
                    background: "#dcfce7",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {stock}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Watchlist */}
        {summary.watchlist.length > 0 && (
          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <div style={{ minWidth: 170, fontWeight: 600 }}>⚠ Watchlist</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {summary.watchlist.map((stock) => (
                <span
                  key={stock}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 14,
                    background: "#fef9c3",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {stock}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Avoid */}
        {summary.avoid.length > 0 && (
          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ minWidth: 170, fontWeight: 600 }}>❌ Avoid</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {summary.avoid.map((stock) => (
                <span
                  key={stock}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 14,
                    background: "#fee2e2",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {stock}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MarketTrend;
